import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../api/client";
import { useAppStore } from "../../store";

export function CalendarSettings() {
  const queryClient = useQueryClient();
  const { data: connections = [], isLoading } = useQuery({
    queryKey: ["calendar-connections"],
    queryFn: api.calendar.getConnections,
  });

  const disconnect = useMutation({
    mutationFn: (provider: string) => api.calendar.disconnect(provider),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["calendar-connections"] }),
  });

  const googleConn = connections.find((c) => c.provider === "google");
  const msConn = connections.find((c) => c.provider === "microsoft");
  const apiBase = import.meta.env.VITE_API_BASE ?? "";
  const token = useAppStore((s) => s.authToken);

  function connectUrl(provider: "google" | "microsoft") {
    const base = `${apiBase}/api/calendar/${provider}/connect`;
    return token ? `${base}?token=${token}` : base;
  }

  if (isLoading) return <div className="text-text-muted text-caption">Loading...</div>;

  return (
    <div className="space-y-3">
      <h4 className="text-label text-text-muted">Calendar Connections</h4>

      {/* Google Calendar */}
      <div className="surface-card p-3 flex items-center justify-between">
        <div>
          <div className="text-caption font-medium text-text-primary">Google Calendar</div>
          <div className={`text-micro mt-0.5 ${googleConn ? "text-finish" : "text-text-muted"}`}>
            {googleConn ? `Connected · ${googleConn.email || ""}` : "Not connected"}
          </div>
        </div>
        {googleConn ? (
          <button
            onClick={() => disconnect.mutate("google")}
            disabled={disconnect.isPending}
            className="text-p1 text-micro hover:underline bg-transparent border-none cursor-pointer disabled:opacity-50"
          >
            Disconnect
          </button>
        ) : (
          <a
            href={connectUrl("google")}
            className="text-accent text-micro no-underline hover:underline"
          >
            Connect
          </a>
        )}
      </div>

      {/* Microsoft / Work Calendar */}
      <div className="surface-card p-3 flex items-center justify-between">
        <div>
          <div className="text-caption font-medium text-text-primary">Work Calendar (Microsoft)</div>
          <div className={`text-micro mt-0.5 ${msConn ? "text-finish" : "text-text-muted"}`}>
            {msConn ? `Connected · ${msConn.email || ""}` : "Not connected"}
          </div>
        </div>
        {msConn ? (
          <button
            onClick={() => disconnect.mutate("microsoft")}
            disabled={disconnect.isPending}
            className="text-p1 text-micro hover:underline bg-transparent border-none cursor-pointer disabled:opacity-50"
          >
            Disconnect
          </button>
        ) : (
          <a
            href={connectUrl("microsoft")}
            className="text-accent text-micro no-underline hover:underline"
          >
            Connect
          </a>
        )}
      </div>
    </div>
  );
}
